import {Helmet} from '@dr.pogodin/react-helmet';
import {
  DataStatus,
  Form,
  FormSubmitHandler,
  showNotification,
  Tabs,
  WithoutBlocker,
} from 'platform/components';
import {DeepPartial} from 'utility-types';

import {useRef} from 'react';
import {FieldErrors} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {defaultTo, isNil} from 'ramda';
import {isBoolean, isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetBranchListQuery, useGetSeriesListQuery, useGetTenantQuery} from '@dms/api/core';
import {
  useGetAuthorizationProfileQuery,
  useGetAuthorizationProfilesQuery,
  useLazyGetAuthorizationProfileQuery,
} from '@dms/api/security';
import {
  PostDirectSaleVariantRequest,
  useGetDirectSaleTypesQuery,
  useGetDirectSaleVariantDefaultVarehousesPriorityQuery,
  useGetDirectSaleVariantQuery,
  useGetMaterialPriceTypesQuery,
  useGetWorkRateTypesQuery,
  usePatchDirectSaleVariantMutation,
  usePostDirectSaleVariantMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {
  decimalValueToPercentage,
  percentageToDecimalValue,
  SettingsTemplate,
  SettingsTemplateHeader,
} from '@dms/settings/shared';
import {
  handleApiError,
  queryParams,
  useDocumentTemplates,
  useDuplicateErrorHandler,
  usePermissions,
} from '@dms/shared';

import {useNavigate, useQueryState, stringToBoolean, ApiError} from 'shared/web';

import {AccountingTab} from './(sections)/AccountingTab/AccountingTab';
import {DocumentsTab} from './(sections)/DocumentTab/DocumentsTab';
import {GeneralTab} from './(sections)/GeneralTab/GeneralTab';
import {PaymentTab} from './(sections)/PaymentTab/PaymentTab';
import {ReportingTab} from './(sections)/ReportingTab/ReportingTab';
import {WarehousesTab} from './(sections)/WarehousesTab/WarehousesTab';
import {
  WarehouseDirectSaleVariantsDetailFormSchema,
  WarehouseDirectSaleVariantsDetailFormSchemaType,
} from './types/WarehouseDirectSaleVariantsDetailFormSchema';

export function WarehouseDirectSaleVariantsDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const withoutBlockerRef = useRef<WithoutBlocker>((callback) => callback());
  const {duplicateErrorHandler} = useDuplicateErrorHandler();
  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);
  const [, {isLoading: isTemplatesLoading}] = useDocumentTemplates();

  const [warehouseDictionaryEdit, warehouseDictionaryCreate] = usePermissions({
    permissionKeys: ['warehouseDictionaryEdit', 'warehouseDictionaryCreate'],
  });

  const {
    data: directSaleVariant,
    isLoading: isDirectSaleVariantLoading,
    isError: hasDirectSaleVariantError,
  } = useGetDirectSaleVariantQuery({directSaleVariantId: id as string}, {skip: isNilOrEmpty(id)});

  const {data: tenant, isLoading: isTenantLoading, isError: hasTenantError} = useGetTenantQuery();

  const {
    data: authorizationProfile,
    isLoading: isAuthorizationProfileLoading,
    isError: hasAuthorizationProfileError,
  } = useGetAuthorizationProfileQuery(
    {
      'x-tenant': tenant?.id as string,
      authorizationProfileId: directSaleVariant?.general?.authorizationProfileId as string,
    },
    {skip: isNil(tenant) || isNilOrEmpty(directSaleVariant?.general?.authorizationProfileId)}
  );

  const [lazyGetAuthorizationProfile, {isFetching: isLazyGetAuthorizationProfileFetching}] =
    useLazyGetAuthorizationProfileQuery();

  const {
    data: branchList,
    isLoading: isBranchListLoading,
    isError: hasBranchListError,
  } = useGetBranchListQuery();

  const {
    data: authorizationProfiles,
    isLoading: isAuthorizationProfilesLoading,
    isError: hasAuthorizationProfilesError,
  } = useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id as string}, {skip: isNil(tenant)});

  const {
    data: directSaleTypes,
    isLoading: isDirectSaleTypesLoading,
    isError: hasDirectSaleTypesError,
  } = useGetDirectSaleTypesQuery();

  const {
    data: workRateTypes,
    isLoading: isWorkRateTypesLoading,
    isError: hasWorkRateTypesError,
  } = useGetWorkRateTypesQuery();

  const {
    data: materialPriceTypes,
    isLoading: isMaterialPriceTypesLoading,
    isError: hasMaterialPriceTypesError,
  } = useGetMaterialPriceTypesQuery();

  const {
    data: seriesList,
    isLoading: isSeriesListLoading,
    isError: hasSeriesListError,
  } = useGetSeriesListQuery({
    type: [
      'warehouse/direct-sale',
      'warehouse/offer',
      'accounting/invoice',
      'warehouse/fast-track',
    ],
  });

  const {
    data: directSaleVariantDefaultVarehousesPriority,
    isLoading: isDirectSaleVariantDefaultVarehousesPriorityLoading,
    isError: hasDirectSaleVariantDefaultVarehousesPriorityError,
  } = useGetDirectSaleVariantDefaultVarehousesPriorityQuery();

  const [createDirectSaleVariant] = usePostDirectSaleVariantMutation();
  const [updateDirectSaleVariant] = usePatchDirectSaleVariantMutation();

  const isLoading =
    isDirectSaleVariantLoading ||
    isTenantLoading ||
    isAuthorizationProfilesLoading ||
    isAuthorizationProfileLoading ||
    isBranchListLoading ||
    isDirectSaleTypesLoading ||
    isWorkRateTypesLoading ||
    isMaterialPriceTypesLoading ||
    isSeriesListLoading ||
    isTemplatesLoading ||
    isDirectSaleVariantDefaultVarehousesPriorityLoading;

  const isError =
    hasDirectSaleVariantError ||
    hasTenantError ||
    hasAuthorizationProfilesError ||
    hasAuthorizationProfileError ||
    hasBranchListError ||
    hasDirectSaleTypesError ||
    hasWorkRateTypesError ||
    hasMaterialPriceTypesError ||
    hasSeriesListError ||
    hasDirectSaleVariantDefaultVarehousesPriorityError;

  const handleSubmit: FormSubmitHandler<WarehouseDirectSaleVariantsDetailFormSchemaType> = async (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    // Since branchId is read-only, we don't want to send it back to server
    const {branchId: _branchId, ...rest} = formValues.general;

    const payloadBody = {
      general: {
        ...rest,
        priceTypeRatio: percentageToDecimalValue(rest?.priceTypeRatio ?? 100),
        rateTypeRatio: percentageToDecimalValue(rest?.rateTypeRatio ?? 100),
        minimalMaterialMargin: percentageToDecimalValue(rest?.minimalMaterialMargin ?? 0),
        minimalWorkMargin: percentageToDecimalValue(rest?.minimalWorkMargin ?? 0),
      },
      documents: {
        ...formValues.documents,
        fastTrackIssue: {
          ...formValues.documents.fastTrackIssue,
          // Set both templateId and docSeriesId to null when fastTrack is disabled
          templateId: formValues.documents.fastTrackIssue.isEnable
            ? formValues.documents.fastTrackIssue.templateId || undefined
            : null,
          docSeriesId: formValues.documents.fastTrackIssue.isEnable
            ? formValues.documents.fastTrackIssue.docSeriesId
            : null,
        },
        // Convert empty strings to undefined for optional orderOffer fields
        orderOffer: {
          ...formValues.documents.orderOffer,
          templateId: formValues.documents.orderOffer?.templateId || undefined,
          docSeriesId: formValues.documents.orderOffer?.docSeriesId || undefined,
        },
        // Set all internalPayment properties to null when disabled
        internalPayment: {
          ...formValues.documents.internalPayment,
          invoice: formValues.documents.internalPayment.isEnable
            ? formValues.documents.internalPayment.invoice
            : null,
          invoiceCorrection: formValues.documents.internalPayment.isEnable
            ? formValues.documents.internalPayment.invoiceCorrection
            : null,
        },
      },
      accounting: {
        ...formValues.accounting,
        isTaxDeductible: stringToBoolean(formValues.accounting.isTaxDeductible),
        fixedVatType: stringToBoolean(formValues.accounting.isTaxDeductible)
          ? undefined
          : formValues.accounting.fixedVatType,
      },
      reporting: formValues.reporting,
      warehousePriority: {
        warehouseIds:
          formValues.warehousesPriority.map((warehousePriority) => warehousePriority.warehouseId) ??
          [],
      },
    };

    if (isNotNil(id)) {
      return await updateDirectSaleVariant({
        body: {
          ...payloadBody,
        },
        directSaleVariantId: id,
      })
        .unwrap()
        .then(() => {
          showNotification.success(
            i18n.t('entity.warehouse.notifications.directSaleVariantUpdated')
          );
        })
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          handleNavigateBack();
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await createDirectSaleVariant(payloadBody as PostDirectSaleVariantRequest)
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('entity.warehouse.notifications.directSaleVariantCreated'));
      })
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        handleNavigateBack();
      })
      .catch(duplicateErrorHandler);
  };

  const handleNavigateBack = () => {
    navigate(settingsRoutes.warehousesDirectSaleVariants);
  };

  const handleNavigateToInvalidTab = (
    errors: FieldErrors<WarehouseDirectSaleVariantsDetailFormSchemaType>
  ) => {
    const isGeneralTabValid = isNil(errors.general);

    const isDocumentsTabValid =
      isNil(errors.documents?.fastTrackIssue) && isNil(errors.documents?.orderOffer);

    const isPaymentTabValid =
      isNil(errors.documents?.defaultPaymentType) &&
      isNil(errors.documents?.cardPayment) &&
      isNil(errors.documents?.cashPayment) &&
      isNil(errors.documents?.bankPayment) &&
      isNil(errors.documents?.internalPayment);

    const isAccountingTabValid = isNil(errors.accounting);

    if (!isGeneralTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId('general'));
    }

    if (!isDocumentsTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId('documents'));
    }

    if (!isPaymentTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId('payment'));
    }

    if (!isAccountingTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId('accounting'));
    }
  };

  // Check edit permission only if id is not nil that means that the user is creating a new measurement unit
  const isEditDisabled = isNil(id) ? !warehouseDictionaryCreate : !warehouseDictionaryEdit;

  const header: SettingsTemplateHeader = {
    title: isNil(id)
      ? i18n.t('entity.warehouse.labels.newDirectSaleVariant')
      : (directSaleVariant?.general.name ?? ''),
    breadcrumbs: [
      {
        label: i18n.t('entity.warehouse.labels.directSaleVariants'),
        href: settingsRoutes.warehousesDirectSaleVariants,
      },
    ],
    alerts: isEditDisabled
      ? [
          {
            type: 'banner',
            variant: 'info',
            message: i18n.t('entity.warehouse.labels.missingWarehouseDictionaryEditPermission'),
          },
        ]
      : undefined,
  };

  const defaultValues: DeepPartial<WarehouseDirectSaleVariantsDetailFormSchemaType> = {
    general: {
      ...directSaleVariant?.general,
      isActive: defaultTo(false, directSaleVariant?.general?.isActive),
      branchId: authorizationProfile?.branchId,
      priceTypeRatio: decimalValueToPercentage(
        defaultTo(1, directSaleVariant?.general?.priceTypeRatio)
      ),
      rateTypeRatio: decimalValueToPercentage(
        defaultTo(1, directSaleVariant?.general?.rateTypeRatio)
      ),
      minimalMaterialMargin: decimalValueToPercentage(
        defaultTo(0, directSaleVariant?.general?.minimalMaterialMargin)
      ),
      minimalWorkMargin: decimalValueToPercentage(
        defaultTo(0, directSaleVariant?.general?.minimalWorkMargin)
      ),
      isUnitPriceWithVat: defaultTo(false, directSaleVariant?.general?.isUnitPriceWithVat),
      isChangeableDefaultCustomer: defaultTo(
        false,
        directSaleVariant?.general?.isChangeableDefaultCustomer
      ),
    },
    documents: {
      ...directSaleVariant?.documents,
      fastTrackIssue: {
        ...directSaleVariant?.documents?.fastTrackIssue,
        isEnable: defaultTo(false, directSaleVariant?.documents?.fastTrackIssue?.isEnable),
      },
      cashPayment: {
        ...directSaleVariant?.documents?.cashPayment,
        isEnable: defaultTo(false, directSaleVariant?.documents?.cashPayment?.isEnable),
      },
      cardPayment: {
        ...directSaleVariant?.documents?.cardPayment,
        isEnable: defaultTo(false, directSaleVariant?.documents?.cardPayment?.isEnable),
      },
      bankPayment: {
        ...directSaleVariant?.documents?.bankPayment,
        isEnable: defaultTo(false, directSaleVariant?.documents?.bankPayment?.isEnable),
      },
      internalPayment: {
        ...directSaleVariant?.documents?.internalPayment,
        isEnable: defaultTo(false, directSaleVariant?.documents?.internalPayment?.isEnable),
      },
    },
    accounting: {
      ...directSaleVariant?.accounting,
      isTaxDeductible: isBoolean(directSaleVariant?.accounting?.isTaxDeductible)
        ? (directSaleVariant.accounting.isTaxDeductible as boolean).toString()
        : 'true',
      excludedServiceCenterIds: defaultTo(
        [],
        directSaleVariant?.accounting?.excludedServiceCenterIds
      ),
      isDefaultServiceCenterLocked: defaultTo(
        false,
        directSaleVariant?.accounting?.isDefaultServiceCenterLocked
      ),
      isDefaultLabourWorkItemGroupLocked: defaultTo(
        false,
        directSaleVariant?.accounting?.isDefaultLabourWorkItemGroupLocked
      ),
      isDefaultMaterialWorkItemGroupLocked: defaultTo(
        false,
        directSaleVariant?.accounting?.isDefaultMaterialWorkItemGroupLocked
      ),
    },
    reporting: {
      ...directSaleVariant?.reporting,
      excludedServiceOrderGroupIds: defaultTo(
        [],
        directSaleVariant?.reporting?.excludedServiceOrderGroupIds
      ),
      isDefaultServiceOrderGroupLocked: defaultTo(
        false,
        directSaleVariant?.reporting?.isDefaultServiceOrderGroupLocked
      ),
      isPreDeliveryInspection: defaultTo(
        false,
        directSaleVariant?.reporting?.isPreDeliveryInspection
      ),
    },
    warehousesPriority: id
      ? (directSaleVariant?.warehousesPriority ?? [])
      : directSaleVariantDefaultVarehousesPriority,
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.directSaleVariantDetail')} />
      <DataStatus isLoading={isLoading} isError={isError} minHeight={60}>
        <Form<WarehouseDirectSaleVariantsDetailFormSchemaType>
          mode="onChange"
          isFullHeight
          shouldWatchForUnsavedChanges
          isDisabled={isEditDisabled}
          experimentalZodSchema={WarehouseDirectSaleVariantsDetailFormSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onInvalidSubmit={(errors) => handleNavigateToInvalidTab(errors)}
        >
          {(control, formApi, _navigateWithoutBlocker, withoutBlocker) => {
            withoutBlockerRef.current = withoutBlocker;

            const handleAuthorizationProfileChange = (
              authorizationProfileId: string | string[] | number | null
            ) => {
              if (isNil(tenant) || isNilOrEmpty(authorizationProfileId)) {
                return;
              }

              lazyGetAuthorizationProfile({
                'x-tenant': tenant?.id,
                authorizationProfileId: authorizationProfileId as string,
              })
                .unwrap()
                .then((authorizationProfile) =>
                  formApi.setValue('general.branchId', authorizationProfile.branchId, {
                    shouldValidate: true,
                  })
                )
                .catch(handleApiError);
            };

            return (
              <SettingsTemplate
                header={header}
                data-testid={testIds.settings.warehousesDirectSaleVariantsDetail('template')}
              >
                <Tabs
                  isFullHeight
                  variant="condensed"
                  activeTabId={activeTabId}
                  onChange={(tabId) => withoutBlocker(() => setActiveTabId(tabId))}
                  tabs={[
                    {
                      id: 'general',
                      title: i18n.t('entity.warehouse.labels.general'),
                      content: (
                        <GeneralTab
                          control={control}
                          formApi={formApi}
                          isFetchingAuthorizationProfile={isLazyGetAuthorizationProfileFetching}
                          onDiscard={handleNavigateBack}
                          onAuthorizationProfileChange={handleAuthorizationProfileChange}
                          directSaleVariantId={id}
                          authorizationProfiles={authorizationProfiles}
                          branchList={branchList}
                          directSaleTypes={directSaleTypes}
                          workRateTypes={workRateTypes}
                          materialPriceTypes={materialPriceTypes}
                          seriesList={seriesList}
                          data-testid={testIds.settings.warehousesDirectSaleVariantsDetail(
                            'tab.general'
                          )}
                        />
                      ),
                    },
                    {
                      id: 'documents',
                      title: i18n.t('entity.warehouse.labels.documents'),
                      content: (
                        <DocumentsTab
                          control={control}
                          formApi={formApi}
                          onDiscard={handleNavigateBack}
                          directSaleVariantId={id}
                          seriesList={seriesList}
                          data-testid={testIds.settings.warehousesDirectSaleVariantsDetail(
                            'tab.documents'
                          )}
                        />
                      ),
                    },
                    {
                      id: 'payment',
                      title: i18n.t('entity.warehouse.labels.paymentType'),
                      content: (
                        <PaymentTab
                          control={control}
                          formApi={formApi}
                          onDiscard={handleNavigateBack}
                          directSaleVariantId={id}
                          seriesList={seriesList}
                          data-testid={testIds.settings.warehousesDirectSaleVariantsDetail(
                            'tab.payment'
                          )}
                        />
                      ),
                    },
                    {
                      id: 'accounting',
                      title: i18n.t('entity.warehouse.labels.accounting'),
                      content: (
                        <AccountingTab
                          control={control}
                          formApi={formApi}
                          onDiscard={handleNavigateBack}
                          directSaleVariantId={id}
                          seriesList={seriesList}
                          data-testid={testIds.settings.warehousesDirectSaleVariantsDetail(
                            'tab.accounting'
                          )}
                        />
                      ),
                    },
                    {
                      id: 'reporting',
                      title: i18n.t('entity.warehouse.labels.reporting'),
                      content: (
                        <ReportingTab
                          control={control}
                          formApi={formApi}
                          onDiscard={handleNavigateBack}
                          directSaleVariantId={id}
                          seriesList={seriesList}
                          data-testid={testIds.settings.warehousesDirectSaleVariantsDetail(
                            'tab.reporting'
                          )}
                        />
                      ),
                    },
                    {
                      id: 'warehouses',
                      title: i18n.t('entity.warehouse.labels.warehouses'),
                      content: (
                        <WarehousesTab
                          control={control}
                          formApi={formApi}
                          onDiscard={handleNavigateBack}
                          directSaleVariantId={id}
                          data-testid={testIds.settings.warehousesDirectSaleVariantsDetail(
                            'tab.warehouses'
                          )}
                        />
                      ),
                    },
                  ]}
                />
              </SettingsTemplate>
            );
          }}
        </Form>
      </DataStatus>
    </>
  );
}
